import { jsx, jsxs } from 'react/jsx-runtime';
import { useAuth0 } from '@auth0/auth0-react';
import { VStack, Flex, Spinner, Text, Box, Heading, SimpleGrid, Field, Input, HStack, Button, Alert, Dialog, Circle, Icon, Spacer, Checkbox, Card, Badge, Link, useDisclosure } from '@chakra-ui/react';
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { EditorDisplaySettings, InspectionSheetSettings as InspectionSheetSettings$1, useCurrentTime, Tooltip, Divider } from '@infraloop/common';
import { useTranslation } from '@infraloop/i18n';
import { patchUser, getErrorMesssage, DefaultSettings, activateBaseFeature, getUser, TAX_BEHAVIOR_LABELS, activateAdditionalFeature, ProductType, GRACE_HOURS_BEFORE_RENEWAL, GENERAL_CONTACT_URL, getPrices, SUBSCRIPTION_START_CONTACT_URL } from '@infraloop/services';
import { merge } from 'lodash';
import { LuSave, LuCreditCard, LuClock, LuUser } from 'react-icons/lu';
import dayjs from 'dayjs';
import { BiCube } from 'react-icons/bi';
import { MdOutlineSummarize, MdCardMembership } from 'react-icons/md';

const AccountInformation = () => {
    const { user, isLoading } = useAuth0();
    return (jsx(VStack, { className: "content-container", children: isLoading ? (jsxs(Flex, { gap: 4, children: [jsx(Spinner, {}), jsx(Text, { fontWeight: "bold", children: "\u8AAD\u307F\u8FBC\u307F\u4E2D\u2026" })] })) : (jsx(Box, { w: "full", maxW: "800px", children: jsxs(VStack, { alignItems: "stretch", bg: "white", borderRadius: "lg", border: "1px solid {colors.gray.200}", shadow: "xs", children: [jsx(Heading, { fontSize: "md", px: 4, py: 1, borderBottom: "1px solid {colors.gray.200}", children: "\u30E6\u30FC\u30B6\u30FC\u60C5\u5831" }), jsx(SimpleGrid, { columns: 2, gap: 4, p: 4, pt: 0, children: jsxs(Field.Root, { children: [jsxs(Field.Label, { children: [jsx(Field.RequiredIndicator, {}), jsx(Text, { fontWeight: "normal", fontSize: "xs", children: "\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9" })] }), jsx(Input, { type: "email", defaultValue: user?.email, readOnly: true })] }) })] }) })) }));
};

const noop$1 = () => { };
/**
 * Seam that hands the shared tab bodies their host-supplied data. Platform feeds
 * it from its Redux store; hatsuly feeds it from a TanStack Query hook. Bodies
 * that need no injected data (e.g. AccountInformation reads Auth0 directly) can
 * ignore it entirely.
 */
const AccountSettingsContext = createContext({
    userProfile: null,
    isUserProfileLoaded: false,
    featureFlags: undefined,
    isSubscriptionValid: undefined,
    hasDeprecatedSubscription: undefined,
    updateUserProfile: noop$1,
    notify: noop$1,
});
const useAccountSettings = () => useContext(AccountSettingsContext);

/**
 * Editor display-settings tab (rail label 3D画面設定). Edits a local draft of the
 * signed-in user's default display config and persists it on save. Reads the
 * profile from the AccountSettingsProvider seam so it works in both hosts.
 */
const EditorSettings = () => {
    // Hooks
    const { t } = useTranslation();
    const { getAccessTokenSilently, isLoading } = useAuth0();
    const { userProfile, updateUserProfile, notify } = useAccountSettings();
    // States
    const [draft, setDraft] = useState(userProfile);
    const [isSaving, setIsSaving] = useState(false);
    /**
     * Seed the local draft once the profile is available.
     */
    useEffect(() => {
        if (userProfile)
            setDraft(userProfile);
    }, [userProfile]);
    /**
     * Handle save settings.
     */
    const handleSave = useCallback(async () => {
        if (!draft)
            return;
        setIsSaving(true);
        try {
            const token = await getAccessTokenSilently();
            if (!token)
                return;
            const updatedUser = await patchUser(token, draft);
            if (!updatedUser)
                return;
            await updateUserProfile(updatedUser);
            notify({ status: 'success', description: '3D画面設定が正常に保存されました' });
        }
        catch (err) {
            notify({ status: 'error', description: getErrorMesssage(err, t) });
        }
        finally {
            setIsSaving(false);
        }
    }, [draft, getAccessTokenSilently, updateUserProfile, notify, t]);
    return (jsx(VStack, { className: "content-container", children: isLoading || !draft ? (jsxs(Flex, { gap: 4, children: [jsx(Spinner, {}), jsx(Text, { fontWeight: "bold", children: "\u8AAD\u307F\u8FBC\u307F\u4E2D\u2026" })] })) : (jsxs(Box, { w: "full", maxW: "800px", children: [jsx(HStack, { mb: 3, children: jsxs(Button, { size: "xs", fontSize: "sm", colorPalette: "blue", onClick: handleSave, loading: isSaving, ml: "auto", children: [jsx(LuSave, {}), t('ui.save')] }) }), jsx(VStack, { alignItems: "stretch", bg: "white", borderRadius: "lg", border: "1px solid {colors.gray.200}", shadow: "xs", children: jsxs(Box, { p: 5, children: [jsxs(Alert.Root, { status: "info", borderRadius: 5, fontSize: "md", children: [jsx(Alert.Indicator, {}), jsx(Alert.Description, { children: "\u4EE5\u4E0B\u306E\u8A2D\u5B9A\u306F\u3001\u65B0\u3057\u304F\u4F5C\u6210\u3055\u308C\u308B\u3059\u3079\u3066\u306E\u30A8\u30EA\u30A2\u306B\u30C7\u30D5\u30A9\u30EB\u30C8\u3068\u3057\u3066\u9069\u7528\u3055\u308C\u307E\u3059\u3002\u3053\u308C\u3089\u306E\u8A2D\u5B9A\u306F\u3001\u500B\u3005\u306E\u30A8\u30EA\u30A2\u306E\u8A2D\u5B9A\u3067\u30A8\u30EA\u30A2\u3054\u3068\u306B\u4E0A\u66F8\u304D\u3059\u308B\u3053\u3068\u304C\u3067\u304D\u307E\u3059\u3002" })] }), jsx(Box, { mb: 5 }), jsx(EditorDisplaySettings, { settings: draft.display_config, onUpdated: (newSettings) => {
                                    setDraft((prev) => prev
                                        ? {
                                            ...prev,
                                            display_config: merge({}, prev.display_config, newSettings),
                                        }
                                        : prev);
                                }, onChange: () => null })] }) })] })) }));
};

/**
 * Inspection-sheet settings tab (rail label 帳票詳細). Edits a local draft of the
 * signed-in user's default sheet config and persists it on save. Reads the
 * profile and its "loaded" flag from the AccountSettingsProvider seam.
 */
const InspectionSheetSettings = () => {
    // Hooks
    const { getAccessTokenSilently } = useAuth0();
    const { t } = useTranslation();
    const { userProfile, isUserProfileLoaded, updateUserProfile, notify } = useAccountSettings();
    // States
    const [settings, setSettings] = useState(DefaultSettings);
    const [isSaving, setIsSaving] = useState(false);
    /**
     * Set settings after user is loaded.
     */
    useEffect(() => {
        if (userProfile) {
            setSettings(userProfile);
        }
    }, [userProfile]);
    /**
     * Handle save settings.
     */
    const handleSave = useCallback(async () => {
        try {
            setIsSaving(true);
            const token = await getAccessTokenSilently();
            const updatedUser = await patchUser(token, settings);
            await updateUserProfile(updatedUser);
            setIsSaving(false);
            notify({ status: 'success', description: '帳票詳細の設定が正常に保存されました' });
        }
        catch (error) {
            setIsSaving(false);
            notify({ status: 'error', description: getErrorMesssage(error, t) });
        }
    }, [settings, getAccessTokenSilently, updateUserProfile, notify, t]);
    return (jsx(VStack, { className: "content-container", children: !isUserProfileLoaded ? (jsxs(Flex, { gap: 4, children: [jsx(Spinner, {}), jsx(Text, { fontWeight: "bold", children: "\u8AAD\u307F\u8FBC\u307F\u4E2D\u2026" })] })) : (jsxs(Box, { w: "full", maxW: "800px", children: [jsx(HStack, { mb: 3, children: jsxs(Button, { size: "xs", fontSize: "sm", colorPalette: "blue", onClick: handleSave, loading: isSaving, ml: "auto", children: [jsx(LuSave, {}), t('ui.save')] }) }), jsx(VStack, { alignItems: "stretch", bg: "white", borderRadius: "lg", border: "1px solid {colors.gray.200}", shadow: "xs", children: jsxs(Box, { p: 5, children: [jsxs(Alert.Root, { status: "info", borderRadius: 5, fontSize: "md", children: [jsx(Alert.Indicator, {}), jsx(Alert.Content, { children: jsx(Alert.Description, { children: "\u4EE5\u4E0B\u306E\u8A2D\u5B9A\u306F\u3001\u65B0\u3057\u304F\u4F5C\u6210\u3055\u308C\u308B\u3059\u3079\u3066\u306E\u5DE5\u4E8B\u306B\u30C7\u30D5\u30A9\u30EB\u30C8\u3068\u3057\u3066\u9069\u7528\u3055\u308C\u307E\u3059\u3002\u3053\u308C\u3089\u306E\u8A2D\u5B9A\u306F\u3001\u500B\u3005\u306E\u5DE5\u4E8B\u306E\u8A2D\u5B9A\u3067\u5DE5\u4E8B\u3054\u3068\u306B\u4E0A\u66F8\u304D\u3059\u308B\u3053\u3068\u304C\u3067\u304D\u307E\u3059\u3002" }) })] }), jsx(Box, { mb: 5 }), jsx(InspectionSheetSettings$1, { settings: settings, onChange: (newSettings) => setSettings((prev) => merge({}, prev, newSettings)) })] }) })] })) }));
};

const BaseFeatureConfirmationModal = ({ disclosure, price }) => {
    // Hooks
    const { getAccessTokenSilently } = useAuth0();
    // Seam
    const { updateUserProfile } = useAccountSettings();
    // States
    const [isLoading, setIsLoading] = useState(false);
    const [agreeUpgrade, setAgreeUpgrade] = useState(false);
    const [agreeUpdate, setAgreeUpdate] = useState(false);
    // Disclosure
    const { open, onClose } = disclosure;
    /**
     * Handle cancellation.
     */
    const handleClose = useCallback(() => {
        if (isLoading)
            return;
        setAgreeUpgrade(false);
        setAgreeUpdate(false);
        onClose();
    }, [onClose, isLoading]);
    /**
     * Handle confirmation.
     */
    const handleConfirm = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = await getAccessTokenSilently();
            if (!token)
                return;
            // Activate the base feature
            await activateBaseFeature(token);
            // Update the user profile
            const userInfo = await getUser(token);
            if (!userInfo)
                return;
            await updateUserProfile(userInfo);
        }
        finally {
            setIsLoading(false);
            handleClose();
        }
    }, [getAccessTokenSilently, handleClose, updateUserProfile]);
    return (jsxs(Dialog.Root, { open: open, onOpenChange: (details) => details.open || handleClose(), size: "xl", children: [jsx(Dialog.Backdrop, {}), jsx(Dialog.Positioner, { children: jsxs(Dialog.Content, { children: [jsx(Dialog.CloseTrigger, {}), jsxs(HStack, { gap: 4, alignItems: "start", w: "100%", children: [jsx(Circle, { bg: "orange.100", color: "orange.600", size: "2rem", position: "relative", top: "1rem", left: "1.5rem", children: jsx(Icon, { as: LuCreditCard, boxSize: "1.2rem" }) }), jsxs(VStack, { alignItems: "start", w: "100%", gap: 0, children: [jsx(Dialog.Header, { children: jsxs(Dialog.Title, { children: [price.name, "\u306E\u6709\u52B9\u5316"] }) }), jsx(Dialog.Body, { whiteSpace: "pre-wrap", children: jsxs(VStack, { alignItems: "start", children: [jsx(Spacer, { m: 2 }), jsxs(Checkbox.Root, { checked: agreeUpgrade, onCheckedChange: () => setAgreeUpgrade(!agreeUpgrade), children: [jsx(Checkbox.HiddenInput, {}), jsx(Checkbox.Control, { children: jsx(Checkbox.Indicator, {}) }), jsxs(Checkbox.Label, { children: ["(\u5FC5\u9808) ", price.name, "\u3092\u6709\u52B9\u5316\u3057\u3001\u5E74\u9593\u306E\u6599\u91D1\u3068\u3057\u3066\u00A5", price.price.toLocaleString(), "(", TAX_BEHAVIOR_LABELS[price.tax_behavior], ") \u3092\u652F\u6255\u3046\u3053\u3068\u306B\u540C\u610F\u3057\u307E\u3059\u3002"] })] }), jsxs(Checkbox.Root, { checked: agreeUpdate, onCheckedChange: () => setAgreeUpdate(!agreeUpdate), children: [jsx(Checkbox.HiddenInput, {}), jsx(Checkbox.Control, { children: jsx(Checkbox.Indicator, {}) }), jsx(Checkbox.Label, { children: "(\u5FC5\u9808) \u5229\u7528\u671F\u9593\u7D42\u4E86\u6642\u306B\u5DE5\u4E8B\u30C7\u30FC\u30BF\u306F\u524A\u9664\u3055\u308C\u307E\u3059\u3002\u30C7\u30FC\u30BF\u306E\u4FDD\u6301\u306B\u306F\u5229\u7528\u671F\u9593\u306E\u66F4\u65B0\u304C\u5FC5\u8981\u3067\u3059\u3002\u66F4\u65B0\u306E\u969B\u306F\u304A\u554F\u3044\u5408\u308F\u305B\u30D5\u30A9\u30FC\u30E0\u307E\u305F\u306F\u5F0A\u793E\u62C5\u5F53\u3078\u3054\u9023\u7D61\u304F\u3060\u3055\u3044\u3002" })] })] }) })] })] }), jsxs(Dialog.Footer, { children: [jsx(Button, { variant: "ghost", mr: 3, onClick: handleClose, disabled: isLoading, children: "\u30AD\u30E3\u30F3\u30BB\u30EB" }), jsx(Button, { colorPalette: "blue", onClick: handleConfirm, disabled: !agreeUpgrade || !agreeUpdate || isLoading, loading: isLoading, children: "\u6709\u52B9\u5316\u3059\u308B" })] })] }) })] }));
};

const HatsulyFeatureConfirmationModal = ({ disclosure, price }) => {
    // Hooks
    const { getAccessTokenSilently } = useAuth0();
    // Seam
    const { updateUserProfile } = useAccountSettings();
    // States
    const [isLoading, setIsLoading] = useState(false);
    const [agreeUpgrade, setAgreeUpgrade] = useState(false);
    // const [agreeUpdate, setAgreeUpdate] = useState(false)
    // Disclosure
    const { open, onClose } = disclosure;
    /**
     * Handle cancellation.
     */
    const handleClose = useCallback(() => {
        if (isLoading)
            return;
        setAgreeUpgrade(false);
        onClose();
    }, [onClose, isLoading]);
    /**
     * Handle confirmation.
     */
    const handleConfirm = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = await getAccessTokenSilently();
            if (!token)
                return;
            await activateAdditionalFeature(token, ProductType.Hatsuly);
            // Update the user profile
            const userInfo = await getUser(token);
            if (!userInfo)
                return;
            await updateUserProfile(userInfo);
        }
        finally {
            setIsLoading(false);
            handleClose();
        }
    }, [getAccessTokenSilently, handleClose, updateUserProfile]);
    return (jsxs(Dialog.Root, { open: open, onOpenChange: (details) => details.open || handleClose(), size: "xl", children: [jsx(Dialog.Backdrop, {}), jsx(Dialog.Positioner, { children: jsxs(Dialog.Content, { children: [jsx(Dialog.CloseTrigger, {}), jsxs(HStack, { gap: 4, alignItems: "start", w: "100%", children: [jsx(Circle, { bg: "orange.100", color: "orange.600", size: "2rem", position: "relative", top: "1rem", left: "1.5rem", children: jsx(Icon, { as: LuCreditCard, boxSize: "1.2rem" }) }), jsxs(VStack, { alignItems: "start", w: "100%", gap: 0, children: [jsx(Dialog.Header, { children: jsxs(Dialog.Title, { children: [price.name, "\u306E\u6709\u52B9\u5316"] }) }), jsx(Dialog.Body, { whiteSpace: "pre-wrap", children: jsxs(VStack, { alignItems: "start", children: [jsx(Spacer, { m: 2 }), jsxs(Checkbox.Root, { checked: agreeUpgrade, onCheckedChange: () => setAgreeUpgrade(!agreeUpgrade), children: [jsx(Checkbox.HiddenInput, {}), jsx(Checkbox.Control, { children: jsx(Checkbox.Indicator, {}) }), jsxs(Checkbox.Label, { children: ["(\u5FC5\u9808) ", price.name, "\u3092\u6709\u52B9\u5316\u3057\u3001\u4ECA\u6708\u306E\u6599\u91D1\u3068\u3057\u3066\u00A5", price.price.toLocaleString(), "(", TAX_BEHAVIOR_LABELS[price.tax_behavior], ") \u3092\u652F\u6255\u3046\u3053\u3068\u306B\u540C\u610F\u3057\u307E\u3059\u3002"] })] })] }) })] })] }), jsxs(Dialog.Footer, { children: [jsx(Button, { variant: "ghost", mr: 3, onClick: handleClose, disabled: isLoading, children: "\u30AD\u30E3\u30F3\u30BB\u30EB" }), jsx(Button, { colorPalette: "blue", onClick: handleConfirm, disabled: !agreeUpgrade || isLoading, loading: isLoading, children: "\u6709\u52B9\u5316\u3059\u308B" })] })] }) })] }));
};

const MarklyFeatureConfirmationModal = ({ disclosure, price }) => {
    // Context
    const { getAccessTokenSilently } = useAuth0();
    // Seam
    const { updateUserProfile } = useAccountSettings();
    // States
    const [isLoading, setIsLoading] = useState(false);
    const [agreeUpgrade, setAgreeUpgrade] = useState(false);
    // const [agreeUpdate, setAgreeUpdate] = useState(false)
    // Disclosure
    const { open, onClose } = disclosure;
    /**
     * Handle cancellation.
     */
    const handleClose = useCallback(() => {
        if (isLoading)
            return;
        setAgreeUpgrade(false);
        onClose();
    }, [onClose, isLoading]);
    /**
     * Handle confirmation.
     */
    const handleConfirm = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = await getAccessTokenSilently();
            if (!token)
                return;
            await activateAdditionalFeature(token, ProductType.Markly);
            // Update the user profile
            const userInfo = await getUser(token);
            if (!userInfo)
                return;
            await updateUserProfile(userInfo);
        }
        finally {
            setIsLoading(false);
            handleClose();
        }
    }, [getAccessTokenSilently, handleClose, updateUserProfile]);
    return (jsxs(Dialog.Root, { open: open, onOpenChange: (details) => details.open || handleClose(), size: "xl", children: [jsx(Dialog.Backdrop, {}), jsx(Dialog.Positioner, { children: jsxs(Dialog.Content, { children: [jsx(Dialog.CloseTrigger, {}), jsxs(HStack, { gap: 4, alignItems: "start", w: "100%", children: [jsx(Circle, { bg: "orange.100", color: "orange.600", size: "2rem", position: "relative", top: "1rem", left: "1.5rem", children: jsx(Icon, { as: LuCreditCard, boxSize: "1.2rem" }) }), jsxs(VStack, { alignItems: "start", w: "100%", gap: 0, children: [jsx(Dialog.Header, { children: jsxs(Dialog.Title, { children: [price.name, "\u306E\u6709\u52B9\u5316"] }) }), jsx(Dialog.Body, { whiteSpace: "pre-wrap", children: jsxs(VStack, { alignItems: "start", children: [jsx(Spacer, { m: 2 }), jsxs(Checkbox.Root, { checked: agreeUpgrade, onCheckedChange: () => setAgreeUpgrade(!agreeUpgrade), children: [jsx(Checkbox.HiddenInput, {}), jsx(Checkbox.Control, { children: jsx(Checkbox.Indicator, {}) }), jsxs(Checkbox.Label, { children: ["(\u5FC5\u9808) ", price.name, "\u3092\u6709\u52B9\u5316\u3057\u3001\u4ECA\u6708\u306E\u6599\u91D1\u3068\u3057\u3066\u00A5", price.price.toLocaleString(), "(", TAX_BEHAVIOR_LABELS[price.tax_behavior], ") \u3092\u652F\u6255\u3046\u3053\u3068\u306B\u540C\u610F\u3057\u307E\u3059\u3002"] })] })] }) })] })] }), jsxs(Dialog.Footer, { children: [jsx(Button, { variant: "ghost", mr: 3, onClick: handleClose, disabled: isLoading, children: "\u30AD\u30E3\u30F3\u30BB\u30EB" }), jsx(Button, { colorPalette: "blue", onClick: handleConfirm, disabled: !agreeUpgrade || isLoading, loading: isLoading, children: "\u6709\u52B9\u5316\u3059\u308B" })] })] }) })] }));
};

const ExpectedExpirationDate = ({ period }) => {
    const currentTimeInTokyo = useCurrentTime('Asia/Tokyo');
    const expectedExpirationDate = period === 'year'
        ? currentTimeInTokyo.add(1, 'year').subtract(1, 'month').endOf('month')
        : currentTimeInTokyo.endOf('month');
    return (jsxs(Text, { color: "gray.600", children: [currentTimeInTokyo.format('M月'), "\u4E2D\u306B\u6709\u52B9\u5316\u3057\u305F\u5834\u5408\u306E\u6709\u52B9\u671F\u9650: ", expectedExpirationDate.format('YYYY年MM月DD日')] }));
};

const SubscriptionPlan = ({ active, disabled, label, price, expirationDate, interval, autoRenewal, onToggleActivation, }) => {
    // Feature activation cannot be toggled after 23:00 on the last day of every month
    // Due to prices could be updated at the end of the month
    const currentTimeInTokyo = useCurrentTime('Asia/Tokyo');
    const isEndOfMonth = currentTimeInTokyo.isSame(currentTimeInTokyo.endOf('month'), 'day');
    const isAfter23 = currentTimeInTokyo.hour() >= 24 - GRACE_HOURS_BEFORE_RENEWAL;
    const endOfMonthRestriction = isEndOfMonth && isAfter23;
    // Calculate the renewal date based on the expiration date
    const renewalDate = useMemo(() => expirationDate && dayjs(expirationDate).add(1, 'day').startOf('day').toDate(), [expirationDate]);
    // Display date is either the renewal date or the expiration date based on auto-renewal status
    const displayDate = useMemo(() => {
        const date = autoRenewal === 'Y' ? renewalDate : expirationDate;
        return date
            ? date.toLocaleDateString('ja-JP', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            })
            : '';
    }, [autoRenewal, renewalDate, expirationDate]);
    const disabledLabel = useMemo(() => {
        if (!price.active)
            return 'このプランの料金が無効化されています。セールスまでお問い合わせください。';
        if (endOfMonthRestriction)
            return '月末の23時以降は機能の有効化ができません。';
        return '';
    }, [price.active, endOfMonthRestriction]);
    // Badge styling is driven by the card's active state (was `.subscription-plan .chakra-badge` in CSS).
    const badgeStyle = {
        bg: active ? 'primary.500' : 'gray.300',
        color: 'white',
        px: 2,
        py: '1px',
        borderRadius: '4px',
        fontSize: 'sm',
        fontWeight: 'normal',
    };
    return (jsx(Tooltip, { label: label, showArrow: true, children: jsxs(Card.Root, { w: "full", px: 5, fontSize: "sm", borderTopWidth: "8px", borderTopStyle: "solid", borderTopColor: active ? 'primary.500' : 'gray.200', children: [jsxs(HStack, { w: "full", py: 2, justifyContent: "space-between", children: [jsx(Heading, { size: "md", children: price.name }), active ? (jsx(Button, { colorPalette: "blue", disabled: true, onClick: onToggleActivation, children: "\u6709\u52B9" })) : (jsx(Tooltip, { label: disabledLabel, showArrow: true, children: jsx(Button, { colorPalette: "blue", disabled: disabled || !price.active || endOfMonthRestriction, onClick: onToggleActivation, children: "\u6709\u52B9\u5316\u3059\u308B" }) }))] }), jsxs(HStack, { w: "full", py: 2, children: [active ? jsx(Badge, { ...badgeStyle, children: "\u6709\u52B9" }) : jsx(Badge, { ...badgeStyle, children: "\u7121\u52B9" }), autoRenewal === 'Y' ? jsx(Badge, { ...badgeStyle, children: "\u81EA\u52D5\u66F4\u65B0" }) : null, active ? (jsxs(Text, { color: "gray.600", children: [autoRenewal === 'Y' ? '次回更新日:' : '有効期限:', " ", displayDate] })) : (jsx(ExpectedExpirationDate, { period: interval })), jsx(Spacer, {}), price.next_subscription.price !== undefined ? (jsxs(VStack, { align: "end", gap: 0, children: [jsxs(Text, { color: "primary.800", fontSize: "lg", fontWeight: "bold", children: ["\u00A5", price.price.toLocaleString(), "/", interval === 'year' ? '年' : '月', " (", TAX_BEHAVIOR_LABELS[price.tax_behavior], ")"] }), jsxs(Text, { color: "gray.600", fontSize: "sm", children: ["(\u6B21\u56DE\u66F4\u65B0", interval === 'year' ? '時' : '後', ": \u00A5", price.next_subscription.price.toLocaleString(), "/", interval === 'year' ? '年' : '月', " (", TAX_BEHAVIOR_LABELS[price.next_subscription.tax_behavior || 'unspecified'], "))"] })] })) : (jsxs(Text, { color: "primary.800", fontSize: "lg", fontWeight: "bold", children: ["\u00A5", price.price.toLocaleString(), "/", interval === 'year' ? '年' : '月', " (", TAX_BEHAVIOR_LABELS[price.tax_behavior], ")"] }))] }), active && autoRenewal === 'N' && (jsxs(Text, { w: "full", color: "red.500", fontSize: "sm", fontWeight: "normal", mb: 4, children: ["\u66F4\u65B0\u3057\u306A\u3044\u5834\u5408\u306F\u6709\u52B9\u671F\u9650\u3092\u904E\u304E\u308B\u3068\u30C7\u30FC\u30BF\u304C\u524A\u9664\u3055\u308C\u307E\u3059", jsx("br", {}), "\u66F4\u65B0\u306E\u969B\u306F", jsx(Link, { href: GENERAL_CONTACT_URL, variant: "underline", target: "_blank", children: "\u304A\u554F\u5408\u305B" }), "\u307E\u305F\u306F\u5F0A\u793E\u62C5\u5F53\u3078\u3054\u9023\u7D61\u304F\u3060\u3055\u3044\u3002"] })), jsx(Divider, { borderColor: "gray.300" }), jsx(Box, { w: "full", whiteSpace: "pre-wrap", py: 4, children: price.description })] }) }));
};

const Plan = () => {
    // Seam data
    const { userProfile, featureFlags, notify } = useAccountSettings();
    // Hooks
    const { t } = useTranslation();
    const { isLoading, getAccessTokenSilently } = useAuth0();
    // State
    const [prices, setPrices] = useState(null);
    // Discolure to handle agreeing to upgrade modal
    const agreeToActivateBaseFeatureDisclosure = useDisclosure();
    const agreeToUpgradeDisclosure = useDisclosure();
    const agreeToMarklyFeatureDisclosure = useDisclosure();
    const handleBaseFeatureToggle = useCallback(() => {
        agreeToActivateBaseFeatureDisclosure.onOpen();
    }, [agreeToActivateBaseFeatureDisclosure]);
    const handleFeatureToggle = useCallback(() => {
        agreeToUpgradeDisclosure.onOpen();
    }, [agreeToUpgradeDisclosure]);
    const handleMarklyFeatureToggle = useCallback(() => {
        agreeToMarklyFeatureDisclosure.onOpen();
    }, [agreeToMarklyFeatureDisclosure]);
    // Fetch pricing information
    useEffect(() => {
        void (async () => {
            try {
                // Fetch pricing information
                const token = await getAccessTokenSilently();
                if (!token) {
                    return;
                }
                setPrices(await getPrices(token));
            }
            catch {
                notify({ status: 'error', description: t('errors.subscription.fetch') });
            }
        })();
    }, [getAccessTokenSilently, t, notify]);
    return (jsx(VStack, { className: "content-container", children: isLoading || !prices || !userProfile ? (jsxs(Flex, { gap: 4, h: "full", alignItems: "center", justifyContent: "center", children: [jsx(Spinner, {}), jsx(Text, { fontWeight: "bold", children: t('ui.loading') })] })) : (jsxs(Box, { w: "full", maxW: "800px", children: [userProfile?.need_confirmation !== false ? (jsxs(VStack, { gap: 4, alignItems: "center", children: [jsx(Text, { children: "\u6A5F\u80FD\u3092\u6709\u52B9\u5316\u3059\u308B\u306B\u306F\u3001 3D InfraLoop\u7533\u8FBC\u307F\u30D5\u30A9\u30FC\u30E0\u3088\u308A\u304A\u554F\u3044\u5408\u308F\u305B\u304F\u3060\u3055\u3044\u3002" }), jsx(Link, { colorPalette: "blue", href: SUBSCRIPTION_START_CONTACT_URL, target: "_blank", children: "\u304A\u554F\u3044\u5408\u308F\u305B" })] })) : (jsxs(VStack, { alignItems: "stretch", bg: "white", borderRadius: "lg", border: "1px solid {colors.gray.200}", shadow: "xs", children: [jsx(Heading, { fontSize: "md", px: 4, py: 1, borderBottom: "1px solid {colors.gray.200}", children: "\u30B5\u30D6\u30B9\u30AF\u30EA\u30D7\u30B7\u30E7\u30F3" }), jsxs(VStack, { p: 4, pt: 0, fontSize: "sm", gap: 3, alignItems: "start", children: [jsx(Text, { children: "\u3054\u5229\u7528\u4E2D\u306E\u30B5\u30D6\u30B9\u30AF\u30EA\u30D7\u30B7\u30E7\u30F3\u3092\u4EE5\u4E0B\u306B\u8868\u793A\u3057\u3066\u3044\u307E\u3059\u3002" }), jsx(SubscriptionPlan, { active: userProfile?.owned_products.includes(ProductType.Base), disabled: userProfile?.agreed_tos_version !== featureFlags?.selfManagedSubscriptions.tos_version, price: prices.base_feature, expirationDate: new Date(userProfile.base_feature_end_at), interval: "year", autoRenewal: userProfile.auto_base_feature_renewal, onToggleActivation: handleBaseFeatureToggle }), jsx(SubscriptionPlan, { active: userProfile?.owned_products.includes(ProductType.Hatsuly), disabled: !userProfile?.owned_products.includes(ProductType.Base) ||
                                        userProfile?.owned_products.includes(ProductType.Hatsuly) ||
                                        userProfile?.agreed_tos_version !== featureFlags?.selfManagedSubscriptions.tos_version, label: !userProfile?.owned_products.includes(ProductType.Base)
                                        ? `先に${prices.base_feature.name}を有効化する必要があります。`
                                        : undefined, price: prices.additional_feature, expirationDate: new Date(userProfile.additional_feature_end_at), interval: "month", onToggleActivation: handleFeatureToggle }), jsx(SubscriptionPlan, { active: userProfile?.owned_products.includes(ProductType.Markly), disabled: !userProfile?.owned_products.includes(ProductType.Base) ||
                                        userProfile?.owned_products.includes(ProductType.Markly) ||
                                        userProfile?.agreed_tos_version !== featureFlags?.selfManagedSubscriptions.tos_version, label: !userProfile?.owned_products.includes(ProductType.Base)
                                        ? `先に${prices.base_feature.name}を有効化する必要があります。`
                                        : undefined, price: prices.markly_feature, expirationDate: new Date(userProfile.markly_feature_end_at), interval: "month", onToggleActivation: handleMarklyFeatureToggle })] })] })), jsx(BaseFeatureConfirmationModal, { disclosure: agreeToActivateBaseFeatureDisclosure, price: prices.base_feature }), jsx(HatsulyFeatureConfirmationModal, { disclosure: agreeToUpgradeDisclosure, price: prices.additional_feature }), jsx(MarklyFeatureConfirmationModal, { disclosure: agreeToMarklyFeatureDisclosure, price: prices.markly_feature })] })) }));
};

/**
 * Placeholder shown for account-settings tabs whose real body has not shipped
 * yet. Follow-up slices swap this out for the actual form at the same route.
 */
const ComingSoon = ({ labelKey }) => {
    // Hooks
    const { t } = useTranslation();
    return (jsxs(VStack, { gap: "14px", textAlign: "center", justify: "center", minH: "300px", p: "24px", children: [jsx(Circle, { size: "60px", bg: "primary.50", color: "primary.600", children: jsx(Icon, { as: LuClock, boxSize: "28px" }) }), jsxs(Box, { children: [jsx(Text, { fontSize: "16px", fontWeight: "bold", color: "gray.800", children: t('userAccount.comingSoon.title') }), jsx(Text, { fontSize: "13px", color: "gray.500", mt: "5px", children: t('userAccount.comingSoon.description', { label: t(labelKey) }) })] })] }));
};

/**
 * Account-settings tabs. Values are the URL path segments used by both hosts'
 * routers, so the platform and hatsuly account pages share one route vocabulary.
 */
var AccountSettingsTab;
(function (AccountSettingsTab) {
    AccountSettingsTab["Account"] = "account";
    AccountSettingsTab["InspectionSheet"] = "inspection-sheet-settings";
    AccountSettingsTab["Editor"] = "editor-settings";
    AccountSettingsTab["Billing"] = "billing";
})(AccountSettingsTab || (AccountSettingsTab = {}));
/**
 * Single source of truth for the account-settings navigation. Each host builds
 * its own chrome (platform's full-height sidebar, hatsuly's centered card) from
 * this ordered list so labels, icons, and routes stay in sync across apps.
 */
const ACCOUNT_SETTINGS_TABS = [
    { id: AccountSettingsTab.Account, labelKey: 'userAccount.account.title', icon: LuUser },
    { id: AccountSettingsTab.InspectionSheet, labelKey: 'userAccount.inspectionSheet.title', icon: MdOutlineSummarize },
    { id: AccountSettingsTab.Editor, labelKey: 'userAccount.editor.title', icon: BiCube },
    { id: AccountSettingsTab.Billing, labelKey: 'userAccount.plan.title', icon: MdCardMembership },
];

const noop = () => { };
/**
 * Wraps the account-settings tab bodies and publishes host-supplied data through
 * AccountSettingsContext. Each host supplies its own props: platform from Redux,
 * hatsuly from a TanStack Query hook.
 */
const AccountSettingsProvider = ({ userProfile = null, isUserProfileLoaded = false, featureFlags, isSubscriptionValid, hasDeprecatedSubscription, updateUserProfile, notify, children, }) => {
    // Derived state
    const value = useMemo(() => ({
        userProfile,
        isUserProfileLoaded,
        featureFlags,
        isSubscriptionValid,
        hasDeprecatedSubscription,
        updateUserProfile: updateUserProfile ?? noop,
        notify: notify ?? noop,
    }), [
        userProfile,
        isUserProfileLoaded,
        featureFlags,
        isSubscriptionValid,
        hasDeprecatedSubscription,
        updateUserProfile,
        notify,
    ]);
    return jsx(AccountSettingsContext.Provider, { value: value, children: children });
};

export { ACCOUNT_SETTINGS_TABS, AccountInformation, AccountSettingsProvider, AccountSettingsTab, ComingSoon, EditorSettings, InspectionSheetSettings, Plan, useAccountSettings };
//# sourceMappingURL=index.js.map
